import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, InputNumber, Modal } from 'antd';
import type { Ally } from '@goelectric/shared-types';
import { allyApi } from '../api/allyApi';
import { describeAllyApiError } from '../utils/allyErrors';
import { buildUpdateAllyPayload, type CreateAllyFormState } from '../utils/fiscal';
import { AllyFiscalFields } from './AllyFiscalFields';

interface EditAllyModalProps {
  open: boolean;
  ally: Ally;
  onClose: () => void;
  /** Refresca el detalle tras un guardado exitoso. */
  onSaved: () => void;
}

/** Mapea el aliado al estado del formulario (misma forma que el alta). */
export function allyToFormState(ally: Ally): CreateAllyFormState {
  const isJuridica = ally.personType === 'JURIDICA';
  // El bloque fiscal no ofrece "NIT" para la natural: la inscrita en el RUT se expresa con el
  // checkbox + CC/CE (el número es el de su cédula). Para la jurídica, el tipo lo fija NIT.
  const registeredInRut = !isJuridica && ally.documentType === 'NIT';
  return {
    personType: ally.personType,
    documentType: registeredInRut ? 'CC' : ally.documentType,
    documentNumber: ally.documentNumber,
    checkDigit: ally.checkDigit ?? undefined,
    registeredInRut,
    legalName: isJuridica ? ally.legalName : undefined,
    firstName: isJuridica ? undefined : ally.firstName,
    lastName: isJuridica ? undefined : ally.lastName,
    email: ally.email,
    phone: ally.phone ?? undefined,
    commissionRatePct: ally.commissionRatePct,
  };
}

/**
 * Edición de aliado (TG-118) en un modal de un solo formulario. Reusa `AllyFiscalFields` y
 * `buildUpdateAllyPayload` (misma identidad fiscal y traducción que el alta) más los tres
 * campos de contacto/comercial. No reusa el wizard: en edición no hay paso de administradores
 * (eso es F11, ya en el detalle) y meter un modo al `AllyForm` arriesgaba el alta por menos
 * ganancia que reusar el bloque fiscal.
 *
 * El error se muestra con el `message` real del backend y, si identifica un campo
 * (`ALIADO_DOCUMENT_DUPLICATED` → `documentNumber`), se marca ese campo (ADR 0021).
 */
export function EditAllyModal({ open, ally, onClose, onSaved }: EditAllyModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateAllyFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-carga al abrir. `AllyFiscalFields` ignora su reset de montaje, así el prellenado del
  // RUT/DV de una natural inscrita en el RUT no se pisa (ver el `useRef` de ese componente).
  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    form.resetFields();
    form.setFieldsValue(allyToFormState(ally));
  }, [open, ally, form]);

  const handleFinish = async (values: CreateAllyFormState) => {
    setSubmitting(true);
    setError(null);
    try {
      await allyApi.update(ally.id, buildUpdateAllyPayload(values));
      message.success('Aliado actualizado.');
      onSaved();
      onClose();
    } catch (caught) {
      const { message: detail, field } = describeAllyApiError(
        caught,
        'No se pudo actualizar el aliado.',
      );
      setError(detail);
      if (field) {
        form.setFields([{ name: field as keyof CreateAllyFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Editar aliado"
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={720}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo actualizar el aliado"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <AllyFiscalFields form={form} />

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Ingresa el email de contacto.' },
            { type: 'email', message: 'Ingresa un email válido.' },
          ]}
        >
          <Input autoComplete="off" placeholder="Introduzca el email del aliado" />
        </Form.Item>

        <Form.Item label="Teléfono" name="phone">
          <Input autoComplete="off" placeholder="Introduzca el teléfono del aliado" />
        </Form.Item>

        <Form.Item
          label="% de comisión"
          name="commissionRatePct"
          rules={[
            { required: true, message: 'Ingresa el porcentaje de comisión.' },
            { type: 'number', min: 0, max: 100, message: 'El porcentaje va entre 0 y 100.' },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            precision={2}
            style={{ width: '100%' }}
            placeholder="Introduzca el porcentaje"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
